/**
 * ArchLang v0.10 — `index` (catalog) representation data model (engine layer).
 *
 * `computeIndexData(view, plan, ctx)` turns a `view` carrying an `index { … }`
 * clause into an **`IndexData`**: the ordered list of shown *module* keys the
 * catalog surface draws as cards, plus the authored column count. It is pure over
 * the landed expression evaluator (`evalExpression`) — no scene types, no
 * rendering. The card VISUALS are composed downstream from the module itself
 * (`composeModuleCard`/`composeBoardNode`), so this layer only decides *which*
 * elements appear and in *what order* — the reading counterpart to
 * `computeTableData`'s rows.
 *
 * ## Entries = the shown selection, modules only
 * Rows come from the plan's `shown` keys (the view's `show`/`hide` selection),
 * falling back to the whole node universe for an index-only view. v1 indexes what
 * the card composer handles — **modules** — so non-module shown elements
 * (interfaces, processes, tests) are filtered out (a scoped v2 widens this).
 *
 * ## Ordering
 * `sort by <getter>` orders the cards (nulls last, numbers numerically, else by
 * text); its ABSENCE sorts by display name — the documented `@name` default —
 * with the qualified key as a stable tie-breaker. Deterministic: a pure function
 * of `(view, plan, model, graph)`, no `Math.random` / `Date`.
 */

import type { StableId } from "./types.js";
import type { Expr } from "./selector-ast.js";
import type { IndexClause, ViewDeclV2 } from "./v010-ast.js";
import type { ElementNode, EvalContext } from "./selector-eval.js";
import { evalExpression, UnsupportedExprError, type ExprValue } from "./expression-eval.js";
import type { ViewProjectionPlan } from "./view-projection.js";

// =============================================================================
// Shape
// =============================================================================

/** One catalog entry — the module's qualified key + its stable id (for the card
 *  click-target / selection, mirroring a table row's identity). */
export interface IndexEntry {
  /** The module's qualified key — the id the card composer looks the module up by. */
  readonly key: string;
  /** The module's `#id`, when it carries one — preferred over `key` for identity. */
  readonly id?: StableId;
}

/** The full catalog projection — ordered module entries + the authored column
 *  count (undefined = the surface auto-fits columns to its width). */
export interface IndexData {
  readonly columns?: number;
  readonly entries: readonly IndexEntry[];
}

// A resolved sort key — JSON-clean scalars; `null` = absent (sorts last).
type SortValue = string | number | boolean | null;

// =============================================================================
// computeIndexData
// =============================================================================

/**
 * Project a `view` with an `index` clause into an {@link IndexData}. Returns
 * `null` for a view that carries no `IndexClause` (nothing to catalog).
 */
export function computeIndexData(
  view: ViewDeclV2,
  plan: ViewProjectionPlan | null,
  ctx: EvalContext,
): IndexData | null {
  const index = view.clauses.find((c): c is IndexClause => c.kind === "index");
  if (!index) return null;

  // Entries = the shown selection (plan) or the whole universe (index-only view),
  // filtered to modules — the elements the card composer draws (v1).
  const keys = plan ? [...plan.shown] : ctx.universe.nodes.map((n) => n.key);
  const idByKey = idIndex(plan, ctx);

  const rows: { entry: IndexEntry; sortValue: SortValue; name: string }[] = [];
  for (const key of keys) {
    const subject = ctx.universe.byKey(key);
    if (!subject || subject.structuralKind !== "module") continue; // v1: modules only
    const sortValue = index.sortBy ? evalCell(index.sortBy, subject, ctx) : null;
    const name = subject.path[subject.path.length - 1] ?? key;
    rows.push({ entry: { key, id: idByKey.get(key) }, sortValue, name });
  }

  rows.sort((a, b) => {
    // Explicit `sort by <getter>`; else the `@name` default (by display name).
    const c = index.sortBy ? compareCells(a.sortValue, b.sortValue) : a.name.localeCompare(b.name);
    if (c !== 0) return c;
    return a.entry.key.localeCompare(b.entry.key); // stable tie-break
  });

  return {
    ...(index.columns !== undefined ? { columns: index.columns } : {}),
    entries: rows.map((r) => r.entry),
  };
}

// =============================================================================
// Ordering helpers (self-contained — mirrors computeTableData's cell sort)
// =============================================================================

/** Evaluate a getter to a scalar sort key; unresolved / deferred → `null`. */
function evalCell(expr: Expr, subject: ElementNode, ctx: EvalContext): SortValue {
  let v: ExprValue;
  try {
    v = evalExpression(expr, subject, ctx);
  } catch (e) {
    // Deferred builtins / list-valued aspect getters throw here — no value to
    // sort on, so the entry reads as absent (sorts last).
    if (e instanceof UnsupportedExprError) return null;
    throw e;
  }
  return v === undefined ? null : v;
}

/** Deterministic ordering: nulls last, numbers numerically, else by text. */
function compareCells(a: SortValue, b: SortValue): number {
  const an = a === null;
  const bn = b === null;
  if (an || bn) return an === bn ? 0 : an ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b));
}

/** Qualified key → `#id`, from the plan (when present) or the derived graph. */
function idIndex(plan: ViewProjectionPlan | null, ctx: EvalContext): ReadonlyMap<string, StableId> {
  if (plan) return plan.stableIds;
  const out = new Map<string, StableId>();
  if (ctx.graph) {
    for (const [m, path] of ctx.graph.pathOfModule) {
      if (m.id) out.set(path.join("."), m.id);
    }
  }
  return out;
}
